import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, Info, MapPin, Store } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { usePageHeader } from '../hooks/usePageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PreOwnedPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('featured');
  const { addToCart } = useCart();
  const header = usePageHeader('pre-dived', { heading: 'Pre-Dived Gear', subheading: 'Quality used equipment at great prices. All gear thoroughly inspected and serviced.' });

  // Categories for pre-dived gear - matching Products page style
  const categories = [
    { value: 'featured', label: 'Featured' },
    { value: 'all', label: 'All Pre-Dived' },
    { value: 'curated-sets', label: 'Curated Sets' },
    { value: 'cylinders', label: 'Cylinders' },
    { value: 'regulators', label: 'Regulators' },
    { value: 'bcds', label: 'BCDs' },
    { value: 'computers', label: 'Dive Computers' },
    { value: 'wetsuits', label: 'Wetsuits' },
    { value: 'masks', label: 'Masks' },
    { value: 'fins', label: 'Fins' },
    { value: 'accessories', label: 'Accessories' },
  ];

  useEffect(() => {
    fetchPreOwnedProducts();
  }, [selectedCategory]);

  const fetchPreOwnedProducts = async () => {
    try {
      setLoading(true);
      // For featured, use the backend query parameter
      const url = selectedCategory === 'featured' 
        ? `${API_URL}/products/pre-owned?featured=true`
        : `${API_URL}/products/pre-owned`;
      const response = await axios.get(url);
      let filteredProducts = response.data;
      
      if (selectedCategory !== 'all' && selectedCategory !== 'featured') {
        filteredProducts = response.data.filter(product => {
          const desc = (product.description || '').toLowerCase();
          const name = (product.name || '').toLowerCase();
          const subcategory = (product.subcategory || '').toLowerCase();
          
          // PRIORITY: Use stored subcategory if it exists
          if (subcategory) {
            return subcategory === selectedCategory || 
                   (selectedCategory === 'curated-sets' && (subcategory === 'sets' || subcategory === 'curated-sets'));
          }
          
          // Fallback: Use description-based detection for legacy items WITHOUT subcategory
          // Be more strict - only match if the keyword is a primary descriptor
          switch (selectedCategory) {
            case 'curated-sets':
              // Only match if explicitly a set/bundle, not just containing the word
              return (desc.includes('gear set') || desc.includes('complete set') || desc.includes('bundle') || desc.includes('combo') || desc.includes('package deal'));
            case 'cylinders':
              return (desc.includes('cylinder') || desc.includes('diving tank') || desc.includes('scuba tank'));
            case 'regulators':
              return (desc.includes('regulator') && !desc.includes('gear set'));
            case 'bcds':
              return (desc.includes('bcd') && !desc.includes('gear set'));
            case 'computers':
              return (desc.includes('dive computer') || (desc.includes('computer') && !desc.includes('gear set')));
            case 'wetsuits':
              return (desc.includes('wetsuit') && !desc.includes('gear set'));
            case 'masks':
              return (desc.includes('mask') && !desc.includes('gear set'));
            case 'fins':
              return ((desc.includes('fins') || desc.includes('fin ')) && !desc.includes('gear set'));
            case 'accessories':
              // Only show in accessories if not matched by other categories
              return !desc.includes('regulator') && !desc.includes('bcd') && !desc.includes('wetsuit') && !desc.includes('mask') && !desc.includes('fins') && !desc.includes('cylinder') && !desc.includes('set') && !desc.includes('bundle') && !desc.includes('computer');
            default:
              return true;
          }
        });
      }
      
      setProducts(filteredProducts);
    } catch (error) {
      console.error('Failed to fetch pre-dived products:', error);
      toast.error('Failed to load pre-dived gear');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success(`${product.name} added to cart`);
  };

  // For cylinders category, separate into "in date" and "out of date" sections
  const getCylinderSections = () => {
    if (selectedCategory !== 'cylinders') return null;
    
    const inDateCylinders = products.filter(p => {
      const desc = (p.description || '').toLowerCase();
      return desc.includes('tested') || desc.includes('in date') || desc.includes('serviced') || desc.includes('full');
    });
    
    const outOfDateCylinders = products.filter(p => {
      const desc = (p.description || '').toLowerCase();
      return desc.includes('out of date') || desc.includes('as is') || desc.includes('needs');
    });
    
    return { inDateCylinders, outOfDateCylinders };
  };

  const ProductCard = ({ product }) => (
    <div
      className="bg-white border border-gray-100 rounded-lg overflow-hidden hover:border-[#0A2342]/20 hover:shadow-xl transition-all duration-300 group"
      data-testid={`preowned-card-${product.id}`}
    >
      <Link to={`/products/${product.id}`}>
        <div className="aspect-square overflow-hidden bg-gray-100 flex items-center justify-center">
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      </Link>
      
      <div className="p-6">
        <div className="flex items-start justify-between mb-2">
          <Link to={`/products/${product.id}`}>
            <h3 className="text-xl font-heading font-semibold text-[#0A2342] group-hover:text-[#FF6B35] transition-colors">
              {product.name}
            </h3>
          </Link>
          {product.in_stock ? (
            <Badge className="bg-green-100 text-green-800 border-green-200">Available</Badge>
          ) : (
            <Badge variant="destructive">Sold</Badge>
          )}
        </div>
        
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
          {product.description}
        </p>
        
        {product.sizes && product.sizes.length > 0 && (
          <p className="text-sm text-gray-500 mb-3">Size: {product.sizes.join(', ')}</p>
        )}
        
        <div className="flex items-center justify-between">
          <div>
            <span className="text-2xl font-mono-accent font-bold text-[#0A2342]">
              R{product.price.toLocaleString()}
            </span>
          </div>
          <Button
            onClick={() => handleAddToCart(product)}
            disabled={!product.in_stock}
            className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90 shadow-lg hover:shadow-xl transition-all duration-300"
            data-testid={`add-to-cart-${product.id}`}
          >
            {product.in_stock ? 'Add to Cart' : 'Sold Out'}
          </Button>
        </div>
      </div>
    </div>
  );

  const cylinderSections = getCylinderSections();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A2342]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#0A2342] mb-4">
            {header.heading}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mb-6">
            {header.subheading}
          </p>
          <div className="bg-[#F0EFEA] border border-gray-200 rounded-lg p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-[#0A2342] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <div className="text-sm text-gray-700">
              <strong>Quality Guarantee:</strong> All pre-dived equipment is thoroughly tested, serviced, and backed by our 30-day warranty.
            </div>
          </div>
          
          {/* Pre-Dived Location Notice */}
          <div className="bg-[#0A2342] text-white p-4 rounded-lg mt-4 flex items-start gap-3">
            <Store className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold mb-1">View Pre-Dived Gear In Person</h4>
              <p className="text-sm text-[#FF6B35] font-medium mb-2">By appointment only</p>
              <div className="flex items-start gap-2 text-sm text-gray-300">
                <MapPin className="w-4 h-4 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-white">Just Dive Pre-Owned</p>
                  <p>735 Orkney Cres, Faerie Glen, Pretoria</p>
                  <p className="text-[#FF6B35] mt-1">+2782 579 3732</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-3 mb-12">
          {categories.map((category) => (
            <Button
              key={category.value}
              variant={selectedCategory === category.value ? 'default' : 'outline'}
              onClick={() => setSelectedCategory(category.value)}
              className={selectedCategory === category.value ? 'bg-[#0A2342] text-white' : ''}
              data-testid={`preowned-category-${category.value}-btn`}
            >
              {category.label}
            </Button>
          ))}
        </div>

        {/* Products Display */}
        {products.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-xl text-gray-500 mb-2">No pre-dived gear available in this category</p>
            <p className="text-gray-400 mb-6">Check back soon for new listings!</p>
            <Link to="/products">
              <Button className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90">
                Browse New Gear
              </Button>
            </Link>
          </div>
        ) : selectedCategory === 'cylinders' && cylinderSections ? (
          <>
            {/* Tested and In Date Section */}
            {cylinderSections.inDateCylinders.length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-heading font-bold text-[#0A2342] mb-2">
                  Tested and In Date
                </h2>
                <p className="text-gray-600 mb-6">3000psi</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {cylinderSections.inDateCylinders.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              </div>
            )}
            
            {/* Out of Date Section */}
            {cylinderSections.outOfDateCylinders.length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-heading font-bold text-[#0A2342] mb-2">
                  Out of Date
                </h2>
                <p className="text-gray-600 mb-6">3000psi</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {cylinderSections.outOfDateCylinders.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PreOwnedPage;
